import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, apiError } from "../lib/api";
import { Layout } from "../components/Layout";
import { Disclaimer } from "../components/Disclaimer";
import { Send, Download, Save, Plus, FileCheck2, Archive, ExternalLink, Check, HelpCircle, SkipForward, Trash2, TrendingUp, ShoppingCart } from "lucide-react";
import { Linkify } from "../components/Linkify";
import { toast } from "sonner";

const STATUS_STYLE = { done: "var(--earn)", stuck: "var(--cost)", skipped: "var(--text-2)", todo: "var(--text)" };

export default function Session() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [reqName, setReqName] = useState("");
  const [addingReq, setAddingReq] = useState(false);
  const [newTask, setNewTask] = useState("");
  const [growing, setGrowing] = useState(false);
  const chatEnd = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/sessions/${id}`); setData(data); }
    catch (e) { toast.error("Session not found"); nav("/home"); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async () => {
    if (!text.trim()) return;
    const t = text; setText(""); setSending(true);
    setData((d) => ({ ...d, messages: [...d.messages, { id: "tmp", role: "user", text: t }] }));
    try {
      const { data: res } = await api.post(`/sessions/${id}/messages`, { text: t });
      setData((d) => ({ ...d, messages: [...d.messages.filter((m) => m.id !== "tmp"), res.userMessage, res.mentorMessage] }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const setTaskStatus = async (tid, status) => {
    const { data } = await api.put(`/tasks/${tid}`, { status });
    setData((d) => {
      let tasks = d.tasks.map((x) => (x.id === tid ? data.task : x));
      if (data.newTask) tasks = [...tasks, data.newTask];
      const messages = data.mentorMessage ? [...d.messages, data.mentorMessage] : d.messages;
      return { ...d, tasks, messages };
    });
    if (data.newTask) toast.success("Next step added");
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    const { data: t } = await api.post(`/sessions/${id}/tasks`, { title: newTask, weekNumber: 1 });
    setData((d) => ({ ...d, tasks: [...d.tasks, t] }));
    setNewTask("");
  };

  const grow = async () => {
    setGrowing(true);
    try {
      const { data } = await api.post(`/sessions/${id}/grow`);
      setData((d) => ({ ...d, tasks: [...d.tasks, ...(data.tasks || [])],
        messages: data.mentorMessage ? [...d.messages, data.mentorMessage] : d.messages }));
      toast.success("Growth steps added");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGrowing(false); }
  };

  const addRequirement = async () => {
    if (!reqName.trim()) return;
    setAddingReq(true);
    try {
      const { data: r } = await api.post("/requirements", { name: reqName, sessionId: id });
      setData((d) => ({ ...d, requirements: [...d.requirements, r] }));
      setReqName("");
      toast.success("Requirement added with local links");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAddingReq(false); }
  };

  const download = async () => {
    const { data: res } = await api.get(`/sessions/${id}/text`);
    const blob = new Blob([res.text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = res.filename; a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded .txt");
  };

  const save = async () => { await api.post(`/sessions/${id}/snapshot`); toast.success("Session saved"); };
  const archive = async () => { await api.post(`/sessions/${id}/archive`); toast.success("Archived"); nav("/saved"); };
  const deletePath = async () => {
    if (!window.confirm("Delete this path for good? Its chat, tasks and requirements will be removed. This cannot be undone.")) return;
    try { await api.delete(`/sessions/${id}`); toast.success("Path deleted"); nav("/home"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const clearChat = async () => {
    if (!window.confirm("Clear this path's chat history? Tasks and requirements stay.")) return;
    try { await api.delete(`/sessions/${id}/messages`); setData((d) => ({ ...d, messages: [] })); toast.success("Chat cleared"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return <Layout><div className="text-center py-20 label-mono">Loading session…</div></Layout>;
  const { session, tasks, messages, requirements, tools = [] } = data;

  return (
    <Layout>
      <div className="fadeup space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-mono">Active path — for {session.placeSnapshot?.city}</div>
            <h2 className="font-head font-bold text-2xl mt-1">{session.title}</h2>
            <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{session.summary}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={save} className="pm-btn-ghost flex items-center gap-2 text-sm" data-testid="save-session-btn"><Save className="w-4 h-4" /> Save session</button>
            <button onClick={download} className="pm-btn flex items-center gap-2 text-sm" data-testid="download-txt-button"><Download className="w-4 h-4" /> Download .txt</button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Left: tasks + requirements */}
          <div className="space-y-5">
            <div className="pm-card p-4" data-testid="tasks-panel">
              <div className="flex items-center justify-between mb-1">
                <div className="label-mono">Your progress</div>
                <div className="label-mono" data-testid="progress-count">{tasks.filter((t) => t.status === "done").length} of {tasks.length} done</div>
              </div>
              <div className="h-1.5 w-full mb-3 rounded-full overflow-hidden" style={{ background: "var(--border-subtle)" }}>
                <div style={{ width: `${tasks.length ? (tasks.filter((t) => t.status === "done").length / tasks.length) * 100 : 0}%`, height: "100%", background: "var(--earn)", transition: "width .3s ease" }} />
              </div>
              <div className="space-y-2">
                {tasks.map((t, i) => (
                  <div key={t.id} className="border p-3" style={{ borderColor: t.growth ? "var(--earn)" : "var(--border-subtle)" }} data-testid={`task-${t.id}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="badge" data-testid={`step-num-${t.id}`}>Step {i + 1}</span>
                      {t.growth && <span className="badge" style={{ background: "var(--earn)" }}>Grow</span>}
                      <span className="badge" style={{ background: STATUS_STYLE[t.status] }}>{t.status}</span>
                    </div>
                    <span className="text-sm">{t.title}</span>
                    <div className="flex gap-1.5 mt-2">
                      <button onClick={() => setTaskStatus(t.id, "done")} className="pm-btn-ghost text-xs py-1 px-2 flex items-center gap-1" data-testid={`task-done-${t.id}`}><Check className="w-3 h-3" /> Done</button>
                      <button onClick={() => setTaskStatus(t.id, "stuck")} className="pm-btn-ghost text-xs py-1 px-2 flex items-center gap-1" data-testid={`task-stuck-${t.id}`}><HelpCircle className="w-3 h-3" /> Stuck</button>
                      <button onClick={() => setTaskStatus(t.id, "skipped")} className="pm-btn-ghost text-xs py-1 px-2 flex items-center gap-1" data-testid={`task-skip-${t.id}`}><SkipForward className="w-3 h-3" /> Skip</button>
                    </div>
                  </div>
                ))}
                {tasks.length === 0 && <p className="text-sm" style={{ color: "var(--text-2)" }}>No steps yet.</p>}
              </div>
              <div className="flex gap-2 mt-3">
                <input className="pm-input text-sm" placeholder="Add a step…" value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} data-testid="new-task-input" />
                <button onClick={addTask} className="pm-btn px-3" data-testid="add-task-btn"><Plus className="w-4 h-4" /></button>
              </div>
              <button onClick={grow} disabled={growing} className="pm-btn w-full mt-3 flex items-center justify-center gap-2" data-testid="grow-btn">
                <TrendingUp className="w-4 h-4" /> {growing ? "Finding growth moves…" : "Grow this bigger"}
              </button>
            </div>

            <div className="pm-card p-4" data-testid="requirements-panel">
              <div className="label-mono mb-3 flex items-center gap-2"><FileCheck2 className="w-4 h-4" /> Licenses & requirements</div>
              {requirements.map((r) => (
                <div key={r.id} className="border p-3 mb-2" style={{ borderColor: "var(--border-subtle)" }} data-testid={`req-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{r.name}</span>
                    <span className="badge" style={{ background: r.required ? "var(--warning)" : "var(--text-2)" }}>{r.required ? "required" : "optional"}</span>
                  </div>
                  <div className="label-mono mt-1">{r.jurisdiction}</div>
                  <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{r.why}</p>
                  <div className="mt-2 space-y-1">
                    {(r.links || []).map((lk, i) => (
                      <a key={i} href={lk.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm underline" data-testid={`req-link-${r.id}-${i}`}>
                        <span className="badge" style={{ background: "var(--text)" }}>{lk.label}</span> {lk.title} <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                  {r.schooling && (r.schooling.note || r.schooling.url) && (
                    <div className="mt-2 text-sm">
                      <span className="label-mono">Training: </span>
                      {r.schooling.note}{" "}
                      {r.schooling.url && (
                        <a href={r.schooling.url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1" style={{ color: "#2563eb" }} data-testid={`req-school-${r.id}`}>
                          Get training <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  )}
                  {r.dontBuyYet?.length > 0 && <p className="text-xs mt-2" style={{ color: "var(--cost)" }}>Don't buy yet: {r.dontBuyYet.join(", ")}</p>}
                  <div className="mt-2 p-2" style={{ background: "var(--surface-subtle)" }}>
                    <span className="label-mono">This week: </span><span className="text-sm">{r.thisWeek}</span>
                  </div>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input className="pm-input text-sm" placeholder="e.g. food handler card, business license" value={reqName} onChange={(e) => setReqName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRequirement()} data-testid="req-name-input" />
                <button onClick={addRequirement} disabled={addingReq} className="pm-btn px-3" data-testid="add-req-btn"><Plus className="w-4 h-4" /></button>
              </div>
              {addingReq && <p className="label-mono mt-2">Finding official local links…</p>}
            </div>

            <div className="pm-card p-4" data-testid="tools-panel">
              <div className="label-mono mb-3 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Tools you'll need</div>
              {tools.length === 0 && <p className="text-sm" style={{ color: "var(--text-2)" }}>No special tools flagged for this path.</p>}
              {tools.map((t) => (
                <div key={t.id} className="border p-3 mb-2" style={{ borderColor: "var(--border-subtle)" }} data-testid={`tool-${t.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{t.name}</span>
                    {t.ownedAlready
                      ? <span className="badge" style={{ background: "var(--earn)" }}>you have this</span>
                      : <span className="badge" style={{ background: "var(--warning)" }}>need to buy</span>}
                    {t.estCost && <span className="label-mono">{t.estCost}</span>}
                  </div>
                  {t.why && <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{t.why}</p>}
                  {!t.ownedAlready && t.buyUrl && (
                    <a href={t.buyUrl} target="_blank" rel="noreferrer" className="pm-btn text-xs mt-2 inline-flex items-center gap-1.5" data-testid={`tool-buy-${t.id}`}>
                      <ShoppingCart className="w-3.5 h-3.5" /> Buy — lowest price <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>

            <button onClick={() => nav("/intake")} className="pm-btn-ghost text-sm w-full" data-testid="new-path-btn">Start a new path (archives this one)</button>
            <button onClick={archive} className="pm-btn-ghost text-sm w-full flex items-center justify-center gap-2" data-testid="archive-btn"><Archive className="w-4 h-4" /> Archive this session</button>
            <button onClick={deletePath} className="pm-btn-ghost text-sm w-full flex items-center justify-center gap-2" style={{ borderColor: "var(--warning)", color: "var(--warning)" }} data-testid="delete-path-btn"><Trash2 className="w-4 h-4" /> Delete this path</button>
          </div>

          {/* Right: chat */}
          <div className="pm-card flex flex-col" style={{ height: "70vh" }} data-testid="chat-panel">
            <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border-subtle)" }}>
              <span className="label-mono">Mentor — stays on this step</span>
              <button onClick={clearChat} className="label-mono flex items-center gap-1 hover:opacity-70" data-testid="session-clear-chat-btn"><Trash2 className="w-3.5 h-3.5" /> Clear</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
                  <div className="max-w-[85%] p-3 text-sm" style={m.role === "user"
                    ? { background: "var(--text)", color: "#fff" }
                    : { background: "var(--surface-subtle)", border: "1px solid var(--border-subtle)" }}>
                    <Linkify text={m.text} linkStyle={m.role === "user" ? { color: "#93c5fd" } : undefined} />
                  </div>
                </div>
              ))}
              {sending && <div className="label-mono">Mentor is thinking…</div>}
              <div ref={chatEnd} />
            </div>
            <div className="p-3 border-t flex gap-2" style={{ borderColor: "var(--border-subtle)" }}>
              <input className="pm-input text-sm" placeholder="Ask your mentor…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="chat-input" />
              <button onClick={send} disabled={sending} className="pm-btn px-3" data-testid="chat-send-btn"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
        <Disclaimer compact />
      </div>
    </Layout>
  );
}
